import { useForm, useSelector } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LIQUID_VIRTUAL_FIELDS } from '@tronche2tek/shared-types';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CardSide } from '../../api/admin';
import { deleteSide, sidesQueryOptions, uploadSide } from '../../api/admin';
import { ErrorMessage } from '../common/ErrorMessage';
import { LoadingSpinner } from './shared';
import { fmt } from './utils';

/**
 * The template sides an admin has uploaded: the list, one SVG upload form, and
 * a reminder of the variables a side may use.
 */
export function SidesTab({ onMutate }: { onMutate?: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);

  const { data: sides = [], isLoading: loading } =
    useQuery(sidesQueryOptions());

  const refreshSides = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'sides'] });
    onMutate?.();
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSide(id),
    onSuccess: (_data, id) => {
      if (previewId === id) {
        setPreviewId(null);
      }
      refreshSides();
    },
  });

  const handleDelete = (id: string, name: string) => {
    if (!window.confirm(t('admin.sides.deleteConfirm', { name }))) {
      return;
    }
    deleteMutation.mutate(id);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-4">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-ink">
            {t('admin.sides.title')}
          </h3>
          <p className="mt-0.5 text-sm text-muted">
            {t('admin.sides.count', { count: sides.length })}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className={showForm ? 'btn-ghost' : 'btn-blue'}
        >
          {showForm ? (
            t('admin.sides.cancel')
          ) : (
            <>
              <svg
                className="size-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              {t('admin.sides.uploadSide')}
            </>
          )}
        </button>
      </div>

      {/* Mounted only while open, so each opening starts from a blank form. */}
      {showForm && (
        <SideUploadForm
          onUploaded={() => {
            setShowForm(false);
            refreshSides();
          }}
        />
      )}

      <LiquidVarsHelp />

      {sides.length === 0 && !showForm ? (
        <EmptySides />
      ) : (
        <div className="overflow-hidden border border-rule">
          {sides.map((side, idx) => (
            <SideRow
              key={side.id}
              side={side}
              first={idx === 0}
              open={previewId === side.id}
              onTogglePreview={() =>
                setPreviewId((prev) => (prev === side.id ? null : side.id))
              }
              onDelete={() => handleDelete(side.id, side.name)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** The upload form's fields. The SVG is held as a File, not as a FileList. */
interface SideUploadFields {
  name: string;
  file: File | null;
}

/**
 * Typed explicitly: `useForm` derives its field paths from what this returns,
 * so inferring `null` for `file` would drop it from the paths.
 */
function initialSideUploadFields(): SideUploadFields {
  return { name: '', file: null };
}

/**
 * Name plus SVG. The file arrives through the drop zone rather than a visible
 * input, so it is put into the form by hand; picking one also fills a blank
 * name from the filename.
 */
function SideUploadForm({ onUploaded }: { onUploaded: () => void }) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  // The parent invalidates ['admin', 'sides'] in `onUploaded`.
  // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation
  const upload = useMutation({
    mutationFn: ({ name, file }: { name: string; file: File }) =>
      uploadSide(name, file),
    onSuccess: onUploaded,
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  const form = useForm({
    defaultValues: initialSideUploadFields(),
    onSubmit: ({ value }) => {
      if (!value.file || !value.name.trim()) {
        return;
      }
      setError(null);
      upload.mutate({ name: value.name.trim(), file: value.file });
    },
  });
  const file = useSelector(form.store, (state) => state.values.file);
  const canSubmit = useSelector(
    form.store,
    (state) => !!state.values.file && state.values.name.trim().length > 0,
  );
  const busy = upload.isPending;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      className="space-y-3 border border-rule bg-blue/4 p-5"
    >
      <h4 className="text-sm font-semibold text-ink">
        {t('admin.sides.newSide')}
      </h4>
      <form.Field name="name">
        {(field) => (
          <input
            required
            placeholder={t('admin.sides.sideNamePlaceholder')}
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            className="w-full px-3 py-2 text-sm"
          />
        )}
      </form.Field>
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="block w-full cursor-pointer border-2 border-dashed border-rule bg-bg-soft p-4 text-center"
      >
        {file ? (
          <span className="block text-sm font-medium text-blue">
            {file.name}
          </span>
        ) : (
          <span className="block text-sm text-muted">
            {t('admin.sides.dropSvgText')}{' '}
            <span className="text-blue">{t('admin.sides.dropSvgBrowse')}</span>
          </span>
        )}
      </button>
      <input
        ref={fileRef}
        type="file"
        accept=".svg,image/svg+xml"
        className="hidden"
        onChange={(e) => {
          const picked = e.target.files?.[0] ?? null;
          form.setFieldValue('file', picked);
          if (picked && !form.getFieldValue('name').trim()) {
            form.setFieldValue('name', picked.name.replace(/\.[^.]+$/, ''));
          }
        }}
      />
      {error && <ErrorMessage message={error} size="xs" />}
      <button
        type="submit"
        disabled={busy || !canSubmit}
        className="btn-blue w-full justify-center"
        style={{ opacity: busy || !canSubmit ? 0.4 : 1 }}
      >
        {busy ? t('admin.sides.uploading') : t('admin.sides.saveSide')}
      </button>
    </form>
  );
}

/** The variables a side's SVG may use, read off the set the resolver checks. */
function LiquidVarsHelp() {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5 border border-rule bg-bg px-4 py-3 text-xs text-muted">
      <p className="font-medium text-ink-soft">{t('admin.sides.varsTitle')}</p>
      <p>{t('admin.sides.varsInjected')}</p>
      {/* Read off the set the resolver checks against, not a copy of it. This
          list named the ten Entra attributes as well, and a hardcoded copy
          would have gone on advertising them after they stopped resolving by
          name — the one drift this panel cannot afford, since it is what an
          admin reads before drawing a template. */}
      <div className="flex flex-wrap gap-2">
        {[...LIQUID_VIRTUAL_FIELDS].map((v) => (
          <span
            key={v}
            className="mono border border-rule bg-bg-soft px-1.5 py-0.5 text-blue"
          >{`{{ ${v} }}`}</span>
        ))}
      </div>
      <p>{t('admin.sides.varsBound')}</p>
    </div>
  );
}

/** One uploaded side, with its variables and a foldable preview. */
function SideRow({
  side,
  first,
  open,
  onTogglePreview,
  onDelete,
}: {
  side: CardSide;
  first: boolean;
  open: boolean;
  onTogglePreview: () => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ borderTop: first ? 'none' : '1px solid var(--rule)' }}>
      <div className="flex items-center gap-4 px-4 py-3.5">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-ink">{side.name}</p>
          {side.variables.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {side.variables.map((v) => (
                <span
                  key={v}
                  className="mono border border-blue/25 bg-blue/6 px-1.5 py-0.5 text-xs text-blue"
                >{`{{ ${v} }}`}</span>
              ))}
            </div>
          )}
          <p className="mt-0.5 text-xs text-muted-soft">
            {fmt(side.createdAt)}
          </p>
        </div>
        <button
          type="button"
          onClick={onTogglePreview}
          className={
            open ? 'epi-tag blue cursor-pointer' : 'epi-tag cursor-pointer'
          }
        >
          {open ? t('admin.sides.hide') : t('admin.sides.preview')}
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="shrink-0 text-muted-soft transition-colors hover:text-magenta"
          title={t('admin.sides.delete')}
          aria-label={t('admin.sides.delete')}
        >
          <svg
            className="size-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>
      {open && (
        <div className="flex justify-center border-t border-t-rule bg-bg px-4 py-3">
          <img src={side.svgUrl} alt={side.name} className="w-full max-w-sm" />
        </div>
      )}
    </div>
  );
}

function EmptySides() {
  const { t } = useTranslation();
  return (
    <div className="py-16 text-center text-muted-soft">
      <svg
        className="mx-auto mb-3 size-10 opacity-30"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
        />
      </svg>
      <p className="text-sm">{t('admin.sides.noSides')}</p>
    </div>
  );
}
